import styled from '@emotion/styled'

import { SecondaryButton, XSmallTertiaryButton } from '../../../components/Button'
import MultiLineButton from '../../../components/MultiLineButton'
import { MOBILE } from '../../../guided-toolbox/utils/breakpoints'

export const HELP_ICON_GUTTER = 32
const CHOICE_ROW_GAP = 16
export const CHOICE_MAX_WIDTH = '230px'
// Narrowest a button gets before its label wraps ("Upload a statement" is the widest).
const CHOICE_MIN_WIDTH = 216
// Below this the three buttons can't hold their labels on one line, so the row stacks.
export const CHOICE_STACK_WIDTH = CHOICE_MIN_WIDTH * 3 + CHOICE_ROW_GAP * 2 + HELP_ICON_GUTTER
const CHOICE_STACK_BUTTON_MAX = 500

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  /* Spacing down to the investments below, which the action row used to carry. */
  margin-bottom: 40px;

  ${MOBILE} {
    /* Stretch rather than center so the full-width action buttons inside the balance column keep
       their width; the columns center their own text instead. */
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    text-align: center;
  }
`

/** Heading column, holding anything that reads as part of the page title. */
export const HeaderMain = styled.div`
  min-width: 0;
`

export const PageHeading = styled.h1`
  margin: 0;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 40px;
  font-weight: 400;
  line-height: 1.25;

  ${MOBILE} {
    color: ${p => p.theme.darkestGray ?? '#022a3a'};
    font-size: 30px;
    font-weight: 300;
  }
`

export const BalanceSummary = styled.div`
  flex: 0 0 auto;
  text-align: right;

  ${MOBILE} {
    margin-top: 12px;
    text-align: center;
  }
`

export const BalanceLabel = styled.div`
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  line-height: 24px;
`

export const BalanceValue = styled.div`
  font-size: 40px;
  font-weight: 600;
  line-height: 1.25;
`

/* The design tints only the symbol, the way the shared Money component does, so the two are
   split rather than colored as one run of text. */
export const BalanceSymbol = styled.span`
  /* Design's balance green; no theme token matches it. */
  color: #4d9927;
`

export const BalanceDigits = styled.span`
  color: ${p => p.theme.darkestGray ?? '#022a3a'};
`

export const ReviewNote = styled.p`
  margin: 10px 0 0;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 18px;
  line-height: 24px;
  width: 70%;

  a {
    color: ${p => p.theme.primaryColor ?? '#038ab7'};
    text-decoration: underline;
  }

  ${MOBILE} {
    /* Full width, so centering the text centers it on screen. At 70% the box stays at the left
       and only its own contents are centered, which reads as off-center. */
    width: 100%;
    text-align: center;
  }
`

export const InvestmentActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  /* Anchored to the balance above it, so the offset does not move with the heading column's
     height — a wrapped review note used to push these controls down and leave a gap. */
  margin: 25px 0 0;

  ${MOBILE} {
    flex-direction: column;
    align-items: stretch;

    /* Lead with adding an investment on mobile without reordering the desktop row. */
    > *:last-child {
      order: -1;
    }
  }
`

// Query the card width, not the viewport
const ChoicesContainer = styled.div`
  width: 100%;
  container: investment-choices / inline-size;
`

const ChoiceRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: ${CHOICE_ROW_GAP}px;
  width: 100%;

  button {
    flex: 1 1 0;
    min-width: 0;
    max-width: ${CHOICE_MAX_WIDTH};
  }

  @container investment-choices (max-width: ${CHOICE_STACK_WIDTH - 1}px) {
    /* Reversed so the stack leads with the link choice (see LinkWithHelp). */
    flex-direction: column-reverse;
    gap: 20px;
    max-width: ${CHOICE_STACK_BUTTON_MAX + HELP_ICON_GUTTER * 2}px;
    margin: 0 auto;

    button {
      max-width: none;
    }

    > button {
      flex: 0 0 auto;
      width: auto;
      margin: 0 ${HELP_ICON_GUTTER}px;
    }
  }
`

/** Shows the investment choices as equal-width buttons, stacking them when the card is too narrow. */
export const InvestmentChoiceRow = ({ children, ...props }) => (
  <ChoicesContainer {...props}>
    <ChoiceRow>{children}</ChoiceRow>
  </ChoicesContainer>
)

/** Fits a multi-line choice button within the 230px desktop columns. */
export const InvestmentChoice = styled(MultiLineButton)`
  padding-left: 20px;
  padding-right: 20px;
`

export const AddInvestmentPill = styled(XSmallTertiaryButton)`
  line-height: 22px;
  white-space: nowrap;
`

export const EmptyStateBox = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 56px;
  padding: 16px 20px;
  border: 2px solid ${p => p.theme.primaryLighter ?? '#d4ebf2'};
  border-radius: 10px;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-size: 16px;
  text-align: center;
`

export const SkipButton = styled(SecondaryButton)`
  margin-top: 30px;
`

/** Mobile's skip affordance, which the design draws as a link rather than a button. */
export const SkipTextLink = styled.button`
  display: block;
  margin: 30px auto 0;
  border: none;
  background: none;
  color: ${p => p.theme.mediumGray ?? '#4a606a'};
  font-family: 'Open Sans', 'Helvetica Neue', sans-serif;
  font-size: 16px;
  text-decoration: underline;
  cursor: pointer;
`

export const DoneRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 50px;
`
